import { useEffect, useRef, useState } from 'react';
import { Button, Form, FormInstance, Input, Radio, Select } from 'antd';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faItalic, faBold, faUnderline } from '@fortawesome/free-solid-svg-icons';
import {
	faAlignLeft,
	faAlignCenter,
	faAlignRight,
	IconDefinition,
} from '@fortawesome/pro-duotone-svg-icons';

import { txt } from '../../../../locales/i18n';
import { AnimationCollapse } from './animation';

export const ToggleButton = (props: { propertyKey: string[]; icon: IconDefinition }) => {
	const checkboxRef = useRef<HTMLInputElement>(null);
	const buttomRef = useRef<HTMLButtonElement>(null);
	const [checked, setChecked] = useState(false);

	useEffect(() => {
		const checkbox = checkboxRef.current;
		if (checkbox === null) return;

		setChecked(checkbox.checked);

		const handleChange = (event: any) => {
			const checked = event?.target?.checked ?? false;
			setChecked(checked);
		};

		checkbox.addEventListener('change', handleChange);
		return () => checkbox.removeEventListener('change', handleChange);
	}, []);

	const handleToggleCheckbox = () => {
		const checkbox = checkboxRef.current;
		checkbox?.click();

		const button = buttomRef.current;
		button?.blur();
	};

	return (
		<div>
			<Form.Item name={props.propertyKey} valuePropName="checked" noStyle>
				<input type="checkbox" ref={checkboxRef} style={{ display: 'none' }} />
			</Form.Item>

			<Button
				ref={buttomRef}
				ghost={checked}
				onClick={handleToggleCheckbox}
				type={checked ? 'primary' : 'default'}
			>
				<FontAwesomeIcon icon={props.icon} />
			</Button>
		</div>
	);
};

export type TextPanelProps = {
	propertyKey: string;
	formRef: React.RefObject<FormInstance>;
	onSectionChange: (changedValues: any, values: any) => void;
};

export const TextPanel = (props: TextPanelProps) => {
	return (
		<TextPanelStyled>
			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<div>
					<label>{txt('cms.style')}</label>
					<div style={{ display: 'flex' }}>
						<ToggleButton
							propertyKey={[props.propertyKey, 'isItalic']}
							icon={faItalic}
						/>
						<ToggleButton propertyKey={[props.propertyKey, 'isBold']} icon={faBold} />
						<ToggleButton
							propertyKey={[props.propertyKey, 'isUnderlined']}
							icon={faUnderline}
						/>
					</div>
				</div>

				<div>
					<label>{txt('cms.alignment')}</label>
					<Form.Item name={[props.propertyKey, 'textAlign']}>
						<Radio.Group>
							<Radio.Button value="left">
								<FontAwesomeIcon icon={faAlignLeft} />
							</Radio.Button>

							<Radio.Button value="center">
								<FontAwesomeIcon icon={faAlignCenter} />
							</Radio.Button>

							<Radio.Button value="right">
								<FontAwesomeIcon icon={faAlignRight} />
							</Radio.Button>
						</Radio.Group>
					</Form.Item>
				</div>
			</div>

			<div style={{ display: 'flex', gap: '2ch' }}>
				<div className="form-item-container">
					<label>{txt('cms.font family')}</label>
					<Form.Item name={[props.propertyKey, 'fontFamily']}>
						<Select>
							<Select.Option value="Oswald">Oswald</Select.Option>
							<Select.Option value="Raleway">Raleway</Select.Option>
						</Select>
					</Form.Item>
				</div>

				<div>
					<label>{txt('cms.html tag')}</label>
					<Form.Item name={[props.propertyKey, 'htmlTag']}>
						<Select dropdownMatchSelectWidth={false}>
							<Select.Option value="h1">{txt('cms.header')} 1</Select.Option>
							<Select.Option value="h2">{txt('cms.header')} 2</Select.Option>
							<Select.Option value="h3">{txt('cms.header')} 3</Select.Option>
							<Select.Option value="h4">{txt('cms.header')} 4</Select.Option>
							<Select.Option value="p">{txt('cms.paragraph')}</Select.Option>
						</Select>
					</Form.Item>
				</div>

				<div>
					<label>{txt('cms.color')}</label>
					<Form.Item name={[props.propertyKey, 'color']} noStyle>
						<Input type="color" />
					</Form.Item>
				</div>
			</div>

			<div>
				<Form.Item name={[props.propertyKey, 'text']}>
					<Input.TextArea autoSize placeholder="Aa" />
				</Form.Item>
			</div>

			<AnimationCollapse
				propertyKey={props.propertyKey}
				formRef={props.formRef}
				onSectionChange={props.onSectionChange}
			/>
		</TextPanelStyled>
	);
};

const TextPanelStyled = styled.div`
	.row-container {
		display: flex;
		gap: 2ch;
	}

	.form-item-container {
		flex: 1;
	}
`;
